'use client'

import { FC, useRef } from 'react'
import cx from 'classnames'
import { Panel } from '@/components/Panel/Panel'
import { PanelItem } from '@/components/Panel/PanelItem'
import { PanelLayout } from '@/components/Panel/Panel'
import { Colour, SectionPaddingDefault } from '@/components/constants'
import { CmsPageSectionBase } from '@/components/ComposedPage/types'
import { DynamicImage } from '@/components/DynamicImage'
import { HeroCtas } from './HeroCtas'
import styles from './styles.module.scss'
import { RichTextContent, useRichText } from '@/hooks/useRichText'
import { AppStoreButtons } from '@/components/AppStoreButtons'
import { IdentImageTitle } from '@/components/IdentImageTitle'
import { getAssetUrl } from '@/utils/contentfulAsset'
import { useInitialScroll } from '@/hooks/useInitialScroll'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { LottieAnimation } from '@/components/LottieAnimation/LottieAnimation'

export type CopyPosition = 'leftAligned' | 'rightAligned' | 'center'
export type CopyAlignment = 'center' | 'bottom' | undefined
export type ImagePosition = 'rightAligned' | 'leftAligned' | 'bottom'

export enum AnimationTrigger {
  onScroll = 'onScroll',
  onScrollIntoView = 'onScrollIntoView',
}

export enum ImageFit {
  COVER = 'cover',
  CONTAIN = 'contain',
}

export interface CmsAsset {
  sys: {
    id: string
  }
  fields: {
    title?: string
    description?: string
    file: {
      url: string
      details?: {
        size: number
      }
      fileName: string
      contentType: string
    }
  }
}

export interface CmsResponsiveImage {
  sys: {
    id: string
  }
  fields: {
    title: string
    description?: string
    file: {
      url: string
      details: {
        size: number
        image: {
          width: number
          height: number
        }
      }
      fileName: string
      contentType: string
    }
  }
}

export interface CmsPageHeroSection extends CmsPageSectionBase {
  fields: CmsPageSectionBase['fields'] & {
    title?: string
    titleIdentImage?: CmsResponsiveImage
    subtitle?: string
    bodyCopy?: string
    richText?: RichTextContent
    disclaimerText?: string
    restrictCopyWidth?: boolean
    fontColour?: 'dark' | 'light'
    textShadow?: boolean
    backgroundColour: Colour
    primaryCtaText?: string
    primaryCtaUrl?: string
    secondaryCtaText?: string
    secondaryCtaUrl?: string
    linkUrl?: string
    linkText?: string
    fullHeight?: boolean
    minimumHeight?: 'medium' | 'large'
    padImage?: boolean
    image?: CmsResponsiveImage
    mobileImage?: CmsResponsiveImage
    centerImage?: boolean
    lottieFile?: CmsAsset
    mobileLottieFile?: CmsAsset
    loopAnimation?: boolean
    animationTrigger?: AnimationTrigger
    backgroundImage?: CmsResponsiveImage
    backgroundVideo?: CmsAsset
    imageFit?: ImageFit
    imagePosition?: ImagePosition
    copyPosition?: CopyPosition
    copyAlignment?: CopyAlignment
    footerText?: string
    openAccountButtonText?: string
    showOpenAccountButton?: boolean
    showAppStoreButtons?: boolean
    testIosQuickLink?: string
    testAndroidQuickLink?: string
    iosQuickLink?: string
    androidQuickLink?: string
    pagePosition?: number
    parallax?: boolean
    reducedWidth?: boolean
    blog?: boolean
    downloadButtonText?: string
    downloadableAsset?: CmsAsset
  }
}

export const PageHero: FC<CmsPageHeroSection> = ({ fields }) => {
  const {
    title,
    titleIdentImage,
    subtitle,
    bodyCopy,
    richText,
    disclaimerText,
    restrictCopyWidth,
    backgroundColour,
    copyPosition,
    imagePosition,
    image,
    mobileImage,
    centerImage = false,
    lottieFile,
    mobileLottieFile,
    loopAnimation = false,
    animationTrigger,
    backgroundImage,
    backgroundVideo,
    imageFit,
    primaryCtaText,
    primaryCtaUrl,
    secondaryCtaText,
    secondaryCtaUrl,
    linkUrl,
    linkText,
    footerText,
    openAccountButtonText,
    showOpenAccountButton,
    showAppStoreButtons,
    testIosQuickLink,
    testAndroidQuickLink,
    iosQuickLink,
    androidQuickLink,
    minimumHeight,
    sectionPaddingBottom,
    sectionPaddingTop,
    pagePosition = 0,
    textShadow,
    reducedWidth,
    blog = false,
    downloadButtonText,
    downloadableAsset,
  } = fields

  const animationRef = useRef(null)
  const mobileAnimationRef = useRef(null)
  const animateOnIntersect = useIntersectionObserver(
    animationRef,
    lottieFile && animationTrigger === AnimationTrigger.onScrollIntoView,
  )
  const mobileAnimateOnIntersect = useIntersectionObserver(
    mobileAnimationRef,
    mobileLottieFile && animationTrigger === AnimationTrigger.onScrollIntoView,
  )
  const animateOnScroll = useInitialScroll(
    (lottieFile || mobileLottieFile) &&
      animationTrigger === AnimationTrigger.onScroll,
  )

  const richTextContent = useRichText(richText)
  const rightCopy = (position?: CopyPosition) => position === 'rightAligned'
  const centerCopy = (position?: CopyPosition) => position === 'center'
  const Title = () => {
    const Tag = pagePosition === 0 ? 'h1' : 'h2'
    const getImageAlignment = () =>
      copyPosition === 'rightAligned' ? 'left' : 'right'

    return (
      <div
        className={cx(styles.heroTitle, styles.titleContainer, {
          [styles.hasIdentImage]: Boolean(titleIdentImage),
          [styles.isBlog]: blog,
        })}
      >
        {titleIdentImage ? (
          <IdentImageTitle
            Tag={Tag}
            image={titleIdentImage}
            title={title!}
            imageAlignment={getImageAlignment()}
          />
        ) : (
          <Tag>{title}</Tag>
        )}
      </div>
    )
  }

  return (
    <Panel
      className={cx(styles.pageHero, {
        [styles.isBlog]: blog,
      })}
      colour={Colour[backgroundColour]}
      layout={PanelLayout.full}
      blog={blog}
      short
    >
      <PanelItem
        className={cx(styles.panelItem, {
          [styles.minimumLarge]: minimumHeight === 'large',
          [styles.minimumMedium]: minimumHeight === 'medium',
          [styles.withVideo]: Boolean(backgroundVideo),
        })}
      >
        <div
          className={cx(styles.content, {
            [styles.reducedWidth]: reducedWidth,
            [styles.textShadow]: textShadow,
            [styles.imageLeft]: imagePosition === 'leftAligned',
            [styles.imageBottom]: imagePosition === 'bottom',
          })}
        >
          <div
            className={cx(
              styles.copy,
              styles[sectionPaddingTop || SectionPaddingDefault.TOP],
              styles[sectionPaddingBottom || SectionPaddingDefault.BOTTOM],
              {
                [styles.restrictWidth]: Boolean(restrictCopyWidth),
                [styles.copyRight]: rightCopy(copyPosition),
                [styles.copyCenter]: centerCopy(copyPosition),
              },
            )}
          >
            {title && <Title />}
            {subtitle && <h4>{subtitle}</h4>}
            {bodyCopy && <h5>{bodyCopy}</h5>}

            {richTextContent}
            {disclaimerText && (
              <p className={styles.disclaimerText}>{disclaimerText}</p>
            )}
            {showAppStoreButtons && (
              <AppStoreButtons
                testIosQuickLink={testIosQuickLink}
                testAndroidQuickLink={testAndroidQuickLink}
                iosQuickLink={iosQuickLink}
                androidQuickLink={androidQuickLink}
              />
            )}
            <HeroCtas
              className={styles.heroCtas}
              primaryCtaUrl={primaryCtaUrl}
              primaryCtaText={primaryCtaText}
              secondaryCtaUrl={secondaryCtaUrl}
              secondaryCtaText={secondaryCtaText}
              linkText={linkText}
              linkUrl={linkUrl}
              showOpenAccountButton={showOpenAccountButton}
              openAccountButtonText={openAccountButtonText}
              downloadButtonText={downloadButtonText}
              downloadableAsset={
                downloadableAsset ? downloadableAsset : undefined
              }
              backgroundColour={backgroundColour}
            />
            {footerText && <p className={styles.footerText}>{footerText}</p>}
          </div>
          {image && (
            <div
              className={cx(styles.image, {
                [styles.centerImage]: centerImage,
              })}
            >
              <DynamicImage
                image={image}
                className={cx(styles.desktopImage, {
                  [styles.hasMobileImage]: Boolean(mobileImage),
                })}
                alt={image.fields.title || ''}
                width={image.fields.file.details.image.width}
                height={image.fields.file.details.image.height}
                sizes="(min-width: 2400px) 2400px, 100vw"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: imageFit || ImageFit.COVER,
                }}
              />
              {mobileImage && (
                <DynamicImage
                  image={mobileImage}
                  className={styles.mobileImage}
                  alt={mobileImage.fields.title || ''}
                  width={mobileImage.fields.file.details.image.width}
                  height={mobileImage.fields.file.details.image.height}
                  sizes="(min-width: 2400px) 2400px, 100vw"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: imageFit || ImageFit.COVER,
                  }}
                />
              )}
            </div>
          )}
          {(lottieFile || mobileLottieFile) && (
            <div
              className={cx(styles.lottieContainer, {
                [styles.centerImage]: centerImage,
                [styles.desktopLottie]:
                  Boolean(lottieFile) && !Boolean(mobileLottieFile),
                [styles.mobileLottie]:
                  Boolean(mobileLottieFile) && !Boolean(lottieFile),
              })}
            >
              {lottieFile && (
                <div
                  className={cx(styles.lottieContainer, styles.desktopLottie, {
                    [styles.hasMobileLottie]: Boolean(mobileLottieFile),
                    [styles.centerImage]: centerImage,
                  })}
                  ref={animationRef}
                >
                  <LottieAnimation
                    className={styles.lottie}
                    play={animateOnIntersect || animateOnScroll}
                    path={lottieFile.fields.file.url}
                    loop={loopAnimation}
                  />
                </div>
              )}
              {mobileLottieFile && (
                <div
                  className={cx(styles.lottieContainer, styles.mobileLottie, {
                    [styles.centerImage]: centerImage,
                  })}
                  ref={mobileAnimationRef}
                >
                  <LottieAnimation
                    className={styles.lottie}
                    play={mobileAnimateOnIntersect || animateOnScroll}
                    path={mobileLottieFile.fields.file.url}
                    loop={loopAnimation}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </PanelItem>
      {backgroundImage && (
        <div
          className={cx(styles.backgroundImage, {
            [styles.withVideo]: Boolean(backgroundVideo),
          })}
        >
          <DynamicImage
            image={backgroundImage}
            alt={backgroundImage.fields.title || ''}
            fill
            sizes="100vw"
            style={{ objectFit: ImageFit.COVER }}
          />
        </div>
      )}

      {backgroundVideo && (
        <div className={styles.backgroundImage}>
          <video
            aria-hidden="true"
            disablePictureInPicture
            className={styles.video}
            muted
            autoPlay={true}
            controls={false}
            playsInline
            loop={true}
          >
            <source src={getAssetUrl(backgroundVideo)} type="video/mp4" />
            <div className={styles.unsupported}>
              <p>
                Sorry, your browser does not support HTML Video. Please update
                or switch to a supported browser.
              </p>
            </div>
          </video>
        </div>
      )}
    </Panel>
  )
}

PageHero.displayName = 'Page Hero'
